/**
 * SF-4652: what changed between two versions of a demo build's change set, worked out in the
 * browser (sf-api keeps only the latest version). Items are matched by group and `ref`; an item
 * whose summary, reason or details differ counts as changed.
 */
import type { ChangeSetItem, DemoChangeSet } from './api/demoBuildsClient';

export interface ChangeSetDiff {
  fromVersion: number;
  toVersion: number;
  /** Keys (see itemKey) of items that are new in the later version. */
  added: Set<string>;
  /** Keys of items in both versions whose content differs. */
  changed: Set<string>;
  /** Items taken out, by group name. */
  removed: Record<string, ChangeSetItem[]>;
  /**
   * SF-4847: module rows whose on/off changed between versions (key -> now on or off). The modules
   * step is shown as rows, not group items, so a module change would otherwise count as nothing.
   */
  modules: Map<string, 'on' | 'off'>;
}

export const itemKey = (group: string, ref: string) => `${group}\u0000${ref}`;

/** With module rows, the one-line modules group repeats them, so it is left out of the item diff. */
const isModulesGroup = (set: DemoChangeSet, group: string) =>
  Array.isArray(set.modules) && group.toLowerCase() === 'modules';

const itemsOf = (set: DemoChangeSet) => {
  const out = new Map<string, ChangeSetItem>();
  for (const g of set.groups ?? []) {
    if (isModulesGroup(set, g.group)) continue;
    for (const item of g.items ?? []) out.set(itemKey(g.group, item.ref), item);
  }
  return out;
};

const sameItem = (a: ChangeSetItem, b: ChangeSetItem) =>
  a.summary === b.summary &&
  (a.problem ?? '') === (b.problem ?? '') &&
  JSON.stringify(a.details ?? []) === JSON.stringify(b.details ?? []);

export function diffChangeSets(before: DemoChangeSet, after: DemoChangeSet): ChangeSetDiff {
  const old = itemsOf(before);
  const now = itemsOf(after);
  const added = new Set<string>();
  const changed = new Set<string>();
  for (const [key, item] of now) {
    const prev = old.get(key);
    if (!prev) added.add(key);
    else if (!sameItem(prev, item)) changed.add(key);
  }
  const removed: Record<string, ChangeSetItem[]> = {};
  for (const g of before.groups ?? []) {
    if (isModulesGroup(before, g.group) || isModulesGroup(after, g.group)) continue;
    for (const item of g.items ?? []) {
      if (!now.has(itemKey(g.group, item.ref))) (removed[g.group] ??= []).push(item);
    }
  }
  const modules = new Map<string, 'on' | 'off'>();
  if (Array.isArray(before.modules) && Array.isArray(after.modules)) {
    const wasOn = new Map(before.modules.map((m) => [m.key, m.on]));
    for (const m of after.modules) {
      if (wasOn.has(m.key) && wasOn.get(m.key) !== m.on) modules.set(m.key, m.on ? 'on' : 'off');
    }
  }
  return {
    fromVersion: before.version,
    toVersion: after.version,
    added,
    changed,
    removed,
    modules,
  };
}

export const removedCount = (diff: ChangeSetDiff) =>
  Object.values(diff.removed).reduce((n, items) => n + items.length, 0);

/** "2 added, 1 changed, 1 taken out" (or "no changes to the set"). */
export function describeDiff(diff: ChangeSetDiff): string {
  const parts = [
    diff.added.size && `${diff.added.size} added`,
    diff.changed.size && `${diff.changed.size} changed`,
    removedCount(diff) && `${removedCount(diff)} taken out`,
    diff.modules.size &&
      `${diff.modules.size} ${diff.modules.size === 1 ? 'module' : 'modules'} switched on or off`,
  ].filter(Boolean);
  return parts.length ? parts.join(', ') : 'no changes to the set';
}

/** The company's colours from Eva's "Colours: color: #aa3300, font_color: #fff" detail. */
export function coloursOf(details: string[] | undefined): Array<{ name: string; value: string }> {
  const line = details?.find((d) => d.startsWith('Colours: '));
  if (!line) return [];
  return line
    .slice('Colours: '.length)
    .split(', ')
    .map((pair) => {
      const at = pair.indexOf(': ');
      return at < 0 ? null : { name: pair.slice(0, at), value: pair.slice(at + 2) };
    })
    .filter((c): c is { name: string; value: string } => !!c && c.value !== '');
}

/** The logo address from Eva's "Logo: https://..." detail, only when it is a web address. */
export function logoOf(details: string[] | undefined): string | null {
  const line = details?.find((d) => d.startsWith('Logo: '));
  const url = line?.slice('Logo: '.length).trim();
  return url && /^https?:\/\//i.test(url) ? url : null;
}
